const googleMapsApiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

// Regex patterns for lat/long
const coordPatterns = [
    // Decimal coordinates with optional spaces and direction indicators
    /-?\d+\.?\d*\s*[NS]?\s*,?\s*-?\d+\.?\d*\s*[EW]?/i,
    // Coordinates in parentheses
    /\((-?\d+\.?\d*)\s*,\s*(-?\d+\.?\d*)\)/,
    // Coordinates with "lat"/"long" indicators
    /lat\s*:?\s*(-?\d+\.?\d*)\s*,?\s*lon(?:g|gitude)?\s*:?\s*(-?\d+\.?\d*)/i
  ];

/**
 * Attempts to extract latitude and longitude from a string
 * @param {string} text - The input string to parse for coordinates
 * @returns {Object|null} Object containing lat/lng if valid coordinates found, null otherwise
 */
export const findLatLongFromString = (text) => {
    if (!text || typeof text !== 'string') return null;

    // Check for coordinates first
    for (const pattern of coordPatterns) {
        const match = text.match(pattern);
        if (match) {
            // Extract numbers from the match
            const numbers = match[0].match(/-?\d+\.?\d*/g);
            if (numbers?.length >= 2) {
                const lat = parseFloat(numbers[0]);
                const lng = parseFloat(numbers[1]);
                
                // Validate coordinates
                if (isValidCoordinates(lat, lng)) {
                    return { type: 'coordinates', lat, lng };
                }
            }
        }
    }
    return null;
};

// Helper function to validate coordinate ranges
const isValidCoordinates = (lat, lng) => {
    return lat >= -90 && lat <= 90 && lng >= -180 && lng <= 180;
};

const streetTypes = [
    "street", "st", "road", "rd", "avenue", "ave", "drive", "dr",
    "lane", "ln", "court", "ct", "place", "pl", "boulevard", "blvd", "way",
    "highway", "hwy", "parkway", "pkwy", "expressway", "expy", "freeway", "fwy"
];

const streetTypePattern = streetTypes.join('|'); // Creates: "street|st|road|rd|avenue|ave|..."

// Regex patterns to check for address-like text
const addressIndicators = [
    new RegExp(`\\d+\\s+[A-Za-z\\s]+(?:${streetTypePattern})`, "i"),
    new RegExp(`(?:\\d+\\s+)?[A-Za-z\\s]+(?:${streetTypePattern})`, "i"), // Fixed this line - removed the forward slash
    /\b[A-Z]{2,}\s+\d{4}\b/, // Postcode pattern (e.g., NSW 2000)
    /\b(?:Level|Suite|Unit)\s+\d+/i,
];

/**
 * Geocodes an address using Google's Geocoding API
 * @param {string} address - The address to geocode
 * @param {string} apiKey - Google Maps API key
 * @returns {Promise<Object|null>} Geocoded result or null if failed
 */
const geocodeAddress = async (address) => {
    try {
        const searchText = address.toLowerCase().includes('australia') 
            ? address 
            : `${address}, Australia`;

        const response = await fetch(
            `https://maps.googleapis.com/maps/api/geocode/json?` + 
            `address=${encodeURIComponent(searchText)}` +
            `&components=country:AU` +
            `&key=${googleMapsApiKey}`
        );

        const data = await response.json();
        
        if (data.status === 'OK' && data.results[0]) {
            const result = data.results[0];
            return {
                type: 'address',
                formattedAddress: result.formatted_address,
                lat: result.geometry.location.lat,
                lng: result.geometry.location.lng
            };
        }
        
        console.warn('Geocoding returned no results:', data.status);
        return null;
    } catch (error) {
        console.error('Geocoding failed:', error);
        return null;
    }
};

/**
 * Checks and processes a delivery point string for coordinates or address
 * @param {string} deliveryPoint - The delivery point to check
 * @returns {Promise<Object|null>} Location data or null if invalid
 */
export const geocodeDeliveryPoint = async (deliveryPoint) => {
    console.log('running geocodeDeliveryPoint', deliveryPoint);
    if (!deliveryPoint) return null;

    if (!googleMapsApiKey) {
        console.error('Google Maps API key is required');
        return null;
    }

    // First check for coordinates
    const coordinates = findLatLongFromString(deliveryPoint);
    if (coordinates) return coordinates;

    // Then check if it looks like an address
    const isLikelyAddress = addressIndicators.some((pattern, index) => {
        const matches = pattern.test(deliveryPoint);
        return matches;
    });
    console.log('isLikelyAddress', isLikelyAddress);
    if (isLikelyAddress) {
        return await geocodeAddress(deliveryPoint);
    }
  
    return null;
};